<template>
  <div
    :class="controlClasses"
    class="mds-form-control mds-select"
    @mouseenter="showDisabledHint($event)"
    @mouseleave="hideDisabledHint()"
  >
    <label :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <ul
        v-if="multiple === true && displayedTags.length > 1"
        class="selected-tags"
      >
        <li
          v-for="option of displayedTags"
          :key="'tag_' + option[optionIdentity]"
        >
          <template v-if="haveSelectedTagSlot">
            <!-- @slot Слот содержимого строки выбранного элемента. Отдаёт option. Крестик всегда снаружи. -->
            <slot
              name="selected-tag"
              v-bind:option="option"
            />
          </template>
          <template v-else>
            <span class="label ellipsis">{{ option?.toString() ?? "" }}</span>
          </template>

          <button
            tabindex="-1"
            type="button"
            class="unselect grey"
            @click.stop="onUnselectOption($event, option)"
          >
            <VIcon material="close" />
          </button>
        </li>
      </ul>

      <div class="control-row">

        <input
          ref="searchInput"
          type="text"
          autocomplete="off"
          :disabled="isDisabled"
          :id="uid"
          :placeholder="searchInputPlaceholder"
          :value="searchInputValue"
          @input="onInput($event)"
          @focusin="onInputFocusIn"
          @focusout="onInputFocusOut"
        />

        <button
          tabindex="-1"
          @click="onClear"
          class="clear"
          :class="{ visible: showClearButton }"
          ref="clearButton"
          type="button"
        >
          <VIcon material="cancel" />
        </button>

        <div class="triangle" @click="onTriangleClick">
          <VIcon material="arrow_drop_down" />
        </div>
      </div>

      <div class="messages-row" v-if="isErrorsVisible">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="empty-data" v-if="showEmptyDataText">
          {{ emptyDataText }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>

    <VPopup
      :offset="1"
      :target="'#' + uid"
      :visible="isFocused"
      @keydown="keyDownListeners"
      @update:visible="focusOut"
      align="left"
      inherit-width
      no-pointer
      position="bottom"
      ref="selectPopup"
      class="mds-select-popup-wrapper"
    >
      <div :class="popupClasses" class="mds-select-popup">
        <ul
          v-show="selectedOptions.length > 0 && isSearchable === true"
          class="mds-select-options selected"
        >
          <li
            v-for="option of selectedOptions"
            :key="'selected_' + option[optionIdentity]"
            :class="getOptionClasses(option)"
            @click.stop="onUnselectOption($event, option)"
            class="mds-select-options-item"
          >
            <template v-if="haveOptionSlot">
              <slot name="option" v-bind:option="option" />
            </template>
            <template v-else>
              {{ option?.toString() ?? "" }}
              <div v-if="optionDescription(option)" class="description">
                {{ optionDescription(option) }}
              </div>
            </template>
          </li>
        </ul>

        <ul
          v-show="unselectedOptions.length > 0"
          class="mds-select-options"
          ref="selectOptions"
        >
          <li
            v-for="option of unselectedOptions"
            :key="option[optionIdentity]"
            :class="getOptionClasses(option)"
            :id="optionHtmlId(option)"
            @click.stop="onSelectOption(option, $event)"
            @mouseenter="setHoveredOption(option)"
            @mouseleave="unsetHoveredOption(option)"
            class="mds-select-options-item"
          >
            <template v-if="haveOptionSlot">
              <slot name="option" v-bind:option="option" />
            </template>
            <template v-else>
              {{ option?.toString() ?? "" }}
              <div v-if="optionDescription(option)" class="description">
                {{ optionDescription(option) }}
              </div>
            </template>
          </li>
        </ul>

        <div
          v-show="unselectedOptions.length === 0 && selectedOptions.length === 0"
          class="mds-select-options-empty"
        >
          <div class="loading-icon" v-if="showLoading">
            <i class="fa fa-spinner" />
          </div>

          <template v-else>
            <template v-if="hasEmptyOptionsSlot">
              <!-- @slot Слот вспомогательного текста -->
              <slot name="empty-options" />
            </template>
            <template v-else>
              Ничего не найдено
            </template>
          </template>
        </div>
      </div>
    </VPopup>
  </div>
</template>

<style lang="scss" src="./VSelectV2.scss" />

<script lang="ts">
import Core         from "@/core/Core";
import VBaseControl from "@/core/services/VBaseControl";
import SelectLoader, { type LoaderFn } from "@/lib/components/VSelectV2/SelectLoader";
import VPopup   from "@/lib/components/VPopup/VPopup.vue";
import VIcon    from "@/lib/components/VIcon/VIcon.vue";

export default defineComponent({
  name: "VSelectV2",

  components: { VIcon, VPopup },

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /**
     * Откуда берем данные, варианты
     * - loader
     * - функция сервиса под 1 параметр поиска, напр PartnersService.searchSeller
     * - либо сырые данные
     *
     */
    data: {
      type:    [Function, Object, Array] as PropType<LoaderFn<any> | SelectLoader | Map<any, any> | any[] | null>,
      default: null,
    },
    /** v-model. Значение — объект (single) или массив объектов (multiple). */
    modelValue: {
      type:    [Array, Object, null],
      default: null,
    },
    /** Однонаправленная передача значения через :value (без v-model). Читается, если modelValue не задан. */
    value: {
      type:    [Array, Object, null],
      default: null,
    },
    /** Множественный выбор */
    multiple: {
      type:    Boolean,
      default: false,
    },
    /** Скрыть кнопку очистки (по умолчанию показана) */
    hideClearBtn: { type: Boolean, default: false },
    /**
     * Текст под контролом, когда источник загрузился и опций нет
     * («Нет неоплаченных отгрузок»). Не показывается, пока данные
     * не запрашивались (пустой родитель, прелоад не сработал).
     */
    emptyDataText: { type: String, default: null },
  },

  emits: ['update:modelValue', 'input', 'focus', 'blur'],

  data() {
    return {
      isFocused:     false,
      selectSource:  SelectLoader.from(this.data),
      pattern:       null,
      hoveredOption: null,
      // Снимок выбранных для блока тегов над инпутом. Обновляется только
      // когда контрол не в фокусе — иначе выбор на открытом попапе менял бы
      // высоту блока и весь контрол прыгал бы.
      displayedTags: [],
    };
  },

  computed: {
    /** Приоритет modelValue, потом value (однонаправленная передача). */
    currentValue() {
      if (this.modelValue !== undefined && this.modelValue !== null) {
        return this.modelValue;
      } else {
        return this.value;
      }
    },

    /**
     * Поле-идентификатор опции. Живёт в источнике: он по нему дедуплицирует
     * список, контрол тем же полем строит ключи и сравнивает выбранное.
     */
    optionIdentity(): string {
      return this.selectSource.getIdentity();
    },

    /** «Загрузили и пусто» — показываем emptyDataText. */
    showEmptyDataText(): boolean {
      if (this.emptyDataText === null) {
        return false;
      } else {
        return this.selectSource.isLoadedAndEmpty;
      }
    },

    /**
     * Контрол выключен пропом disabled ИЛИ источник недоступен
     * (зависимый источник с незаполненным родителем).
     */
    isDisabled(): boolean {
      if (this.disabled === true) {
        return true;
      } else {
        return this.selectSource.isDependentValueNotEmpty === false;
      }
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      return result;
    },

    /** labelStyles переопределяем — у инпута исторически width, а не flex. */
    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    /** Поиск перманентный. */
    isSearchable(): boolean {
      return true;
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.isDisabled === true) classes.push('disabled');
      if (this.multiple === true)   classes.push("multiple");
      if (this.isFocused === true)  classes.push("focused");
      if (this.isValueEmpty)        classes.push("empty");
      return classes;
    },

    popupClasses() {
      return {
        "with-search": this.isSearchable,
        multiple:      this.multiple,
      };
    },

    isValueEmpty(): boolean {
      if (!this.currentValue) return true;
      return this.multiple === true
        ? this.$core.utils.isNotEmptyArray(this.currentValue) === false
        : !this.currentValue;
    },

    valueLabel() {
      if (this.isValueEmpty === true) return null;
      if (this.multiple === true) {
        if (this.currentValue.length === 1) {
          return this.currentValue[0]?.toString() ?? "";
        }
        return `Выбрано: ${this.currentValue.length} эл.`;
      }
      return this.currentValue?.toString() ?? "";
    },

    searchInputValue() {
      if (this.isFocused === true) {
        return this.pattern;
      }
      return this.valueLabel;
    },

    searchInputPlaceholder() {
      if (this.isFocused === true) {
        return "Введите для начала поиска";
      }
      return this.placeholder;
    },

    showClearButton(): boolean {
      if (this.hideClearBtn === true) return false;
      if (this.isDisabled === true) return false;
      if (this.isValueEmpty === true) return false;
      return true;
    },

    showLoading(): boolean {
      return this.selectSource.isLoading;
    },

    haveOptionSlot(): boolean {
      return this.$slots.option !== undefined;
    },

    haveSelectedTagSlot(): boolean {
      return this.$slots["selected-tag"] !== undefined;
    },

    /** Опции из источника (он их хранит). */
    options() {
      return this.selectSource.options;
    },

    selectedOptions() {
      if (Array.isArray(this.currentValue)) {
        return this.currentValue;
      } else if (this.currentValue !== null && typeof this.currentValue === "object") {
        return [this.currentValue];
      } else {
        return [];
      }
    },

    /**
     * Опции, доступные для выбора: уже выбранные исключаем. Если источник ищет
     * локально (не отправляет паттерн на бэкенд) — отсеиваем не совпавшие
     * с введённым текстом. Один проход по списку — оба отсева внутри цикла.
     */
    unselectedOptions() {
      let pattern = null;


      // если поиск внутренний, то фильтруем сами по паттену
      if (
        this.selectSource.isLoadOnInputPattern === false
        && this.pattern
      ) {
        pattern = this.pattern.toLowerCase();
      }

      const result = [];

      for (const option of this.options) {
        if (this.isOptionSelected(option) === true) {
          continue;
        }

        if (pattern !== null) {
          const label = (option?.toString() ?? "").toLowerCase();

          if (label.includes(pattern) === false) {
            continue;
          }
        }

        result.push(option);
      }

      return result;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    hasEmptyOptionsSlot(): boolean {
      return this.$slots["empty-options"] !== undefined;
    },

    /**
     * Ключ зависимостей источника — для watch'а перезагрузки при смене
     * родителя. null, если источника нет или он независимый.
     */
    sourceDependencyKey(): string | null {
      return this.selectSource.dependencyKey;
    },
  },

  watch: {
    options(value) {
      const popup = this.$refs.selectPopup;
      if (Array.isArray(value) && value.length > 0) {
        this.hoveredOption = this.unselectedOptions[0];
        this.calculateOptionsWrapScroll(this.hoveredOption);
      }
      this.$nextTick().then(() => (popup ? popup.calculatePosition() : null));
    },
    pattern(value) {
      if (value === null) return;
      this.search(value);
    },
    /**
     * Смена родителя зависимого источника. Перезагружаем список и, если
     * текущее значение больше не консистентно новому списку, сбрасываем его
     * и подставляем новое.
     *
     * Консистентное значение не трогаем: подстановка умолчаний не должна
     * затирать уже сделанный выбор.
     */
    async sourceDependencyKey(newKey, oldKey) {
      if (newKey === oldKey) {
        // родитель тот же — список перезагружать не от чего
      }
      else {
        const isReloaded = await this.selectSource.reloadOnDependantChange();

        if (isReloaded === false) {
          // источник не перезагружается по смене родителя
        }
        else if (this.isValueEmpty === false && this.isValueConsistentWithOptions() === true) {
          if (this.selectSource.isApplied(this.currentValue) === true) {
            /** Значение стоит с прошлой автоподстановки, а не выбрано руками —
             *  пересчитываем его под нового родителя. Список при этом мог и не
             *  измениться: дефолт может зависеть от родителя напрямую. */
            this.applyAutoValue();
          }
          else {
            // выбрано пользователем — не трогаем
          }
        }
        else {
          /** Значение не из этого списка — сбрасываем, дальше подставляем
           *  заново. Пустое onClear переживёт молча. */
          this.onClear();

          this.applyAutoValue();
        }
      }
    },
    currentValue(newVal, oldVal) {
      if (Core.utils.isEqual(newVal, oldVal) === false) {
        this.validate();
      }
      // Блок тегов синхронизируем только вне фокуса. Внутри фокуса выбор
      // копится в modelValue, но теги не пересобираем — контрол не прыгает.
      if (this.isFocused === false) {
        this.syncDisplayedTags();
      }
    },
  },

  created() {
    // Предзагрузка при монтировании, если источник так настроен.
    // После загрузки — автовыбор: единственный вариант предвыбирается
    // всегда, кроме поисковых источников (туда прелоад не доезжает).
    if (this.selectSource.isPreloadOnMount === true) {
      this.selectSource.loadOnMount().then(() => {
        this.autoSelectIfValueEmpty();
      });
    }
    // Статичный источник: единственная опция предвыбирается сразу.
    else if (this.selectSource.isStatic === true) {
      this.autoSelectIfValueEmpty();
    }
    // Начальный снимок тегов из текущего значения.
    this.syncDisplayedTags();
  },

  methods: {
    /** Копирует текущее выбранное в снимок тегов над инпутом. */
    syncDisplayedTags() {
      this.displayedTags = [...this.selectedOptions];
    },

    /** Сравнивает две опции по идентификатору */
    isSameOption(optionOne, optionTwo) { return optionOne[this.optionIdentity] === optionTwo[this.optionIdentity]; },

    /** Проверяет выбрана ли опция */
    isOptionSelected(option) {
      if (!this.currentValue) return false;
      if (this.multiple === true) {
        if (Array.isArray(this.currentValue) === false) {
          console.warn('[Select] currentValue should be array in multiple mode');
          return false;
        }
        return this.currentValue.some(val => this.isSameOption(val, option));
      }
      return this.isSameOption(this.currentValue, option);
    },

    getOptionClasses(option) {
      const result: Record<string, boolean> = {};
      if (this.isOptionSelected(option)) result.selected = true;
      if (this.hoveredOption && this.isSameOption(option, this.hoveredOption)) {
        result.hovered = true;
      }
      return result;
    },

    /**
     * Описание опции для второй строки. Берётся из поля description,
     * либо из метода getDescription(), если поля нет.
     */
    optionDescription(option): string | null {
      if (option?.description) {
        return option.description;
      } else if (typeof option?.getDescription === "function") {
        return option.getDescription();
      } else {
        return null;
      }
    },

    focus() { this.$refs.searchInput.focus(); },

    optionHtmlId(option) {
      const identity = option?.[this.optionIdentity];
      if (identity === null || identity === undefined) return `option_${this.uid}_unknown`;
      return `option_${this.uid}_${String(identity).replace(/\s/, "_")}`;
    },

    onInputFocusIn(event) {
      if (this.isDisabled === true) return;
      if (this.isFocused === false) {
        this.focusIn(event);
      }
    },

    onInputFocusOut(event) {
      if (
        event instanceof FocusEvent
        && event.relatedTarget !== null
        && event.relatedTarget.classList.contains("mds-select-options") === false
      ) {
        if (this.isDisabled === true) return;
        if (this.isFocused === true) {
          this.focusOut();
        }
      }
    },

    async focusIn(event) {
      this.validation.markTouched();

      if (this.$refs.clearButton.contains(event.target) === false) {
        this.hoveredOption = this.unselectedOptions.length > 0
          ? this.unselectedOptions[0]
          : null;

        if (this.hoveredOption !== null) {
          this.calculateOptionsWrapScroll(this.hoveredOption);
        }

        /**
         * Хитрый хак — важен при программном фокусе (например, навигация по Enter).
         *
         * Enter, нажатый для перехода на текущий VSelect, воспринимается как команда
         * самим контролом — нужно отложить установку обработчиков на следующий тик,
         * чтобы исходное событие не задело наше состояние.
         */
        await this.$nextTick();
        this.isFocused = true;
        this.$emit("focus");
        window.addEventListener("keydown", this.keyDownListeners);

        // Предзагрузка при фокусе, если источник так настроен и опции ещё
        // не загружены. Уже загруженные не перезапрашиваем — смена родителя
        // обновляет их сама.
        this.selectSource.loadOnFocus().then((isLoaded) => {
          if (isLoaded === true) {
            this.autoSelectIfValueEmpty();
          }
          else {
            // загрузки не было — подставлять нечего
          }
        });
      }
    },

    focusOut() {
      const wrap = this.$refs.selectOptions;
      if (wrap) wrap.scrollTop = 0;
      this.isFocused     = false;
      this.pattern       = null;
      this.hoveredOption = null;

      window.removeEventListener("keydown", this.keyDownListeners);

      const searchInput = this.$refs.searchInput;
      if (searchInput && typeof searchInput.blur === "function") {
        searchInput.blur();
      }

      // Фокус снят — пересобираем блок тегов из накопленного за сессию выбора.
      this.syncDisplayedTags();

      this.$emit("blur", this.currentValue);
    },

    keyDownListeners(event) {
      if (event.target.tagName !== "INPUT" || event.key === "Enter") {
        event.preventDefault();
      }

      switch (event.key) {
        case "ArrowDown":
          if (this.hoveredOption !== null) {
            const index = this.unselectedOptions.findIndex(
              (option) => option[this.optionIdentity] === this.hoveredOption[this.optionIdentity]
            );
            if (index > -1 && index < this.unselectedOptions.length - 1) {
              this.hoveredOption = this.unselectedOptions[index + 1];
            } else if (index === this.unselectedOptions.length - 1) {
              this.hoveredOption = this.unselectedOptions[0];
            }
          } else {
            this.hoveredOption = this.unselectedOptions[0];
          }
          this.calculateOptionsWrapScroll(this.hoveredOption, event.key);
          break;

        case "ArrowUp":
          if (this.hoveredOption !== null) {
            const index = this.unselectedOptions.findIndex(
              (option) => option[this.optionIdentity] === this.hoveredOption[this.optionIdentity]
            );
            if (index > 0 && index < this.unselectedOptions.length) {
              this.hoveredOption = this.unselectedOptions[index - 1];
            } else if (index === 0) {
              this.hoveredOption = this.unselectedOptions[this.unselectedOptions.length - 1];
            }
          } else {
            this.hoveredOption = this.unselectedOptions[this.unselectedOptions.length - 1];
          }
          this.calculateOptionsWrapScroll(this.hoveredOption, event.key);
          break;

        case "Enter":
          this.onSelectOption(this.hoveredOption);
          break;

        case "Escape":
          this.focusOut();
          break;
      }
    },

    calculateOptionsWrapScroll(option, keyName) {
      const wrap = this.$refs.selectOptions;

      if (wrap && option) {
        const element = wrap.querySelector(`#${this.optionHtmlId(option)}`);
        if (element === null) return;

        switch (keyName) {
          case "ArrowUp":
            if (
              wrap.scrollTop > element.offsetTop
              || element.offsetTop > wrap.scrollTop + wrap.offsetHeight
            ) {
              wrap.scrollTop = element.offsetTop;
            }
            break;
          case "ArrowDown":
            if (
              element.offsetTop + element.offsetHeight > wrap.offsetHeight + wrap.scrollTop
              || wrap.scrollTop > element.offsetTop + element.offsetHeight
            ) {
              wrap.scrollTop = element.offsetTop + element.offsetHeight - wrap.offsetHeight;
            } else if (element.offsetTop === 0) {
              wrap.scrollTop = 0;
            }
            break;
          default:
            if (element.offsetTop + element.offsetHeight < wrap.scrollTop) {
              wrap.scrollTop = element.offsetTop;
            } else if (wrap.offsetHeight + wrap.scrollTop > element.offsetTop) {
              wrap.scrollTop = element.offsetTop + element.offsetHeight - wrap.offsetHeight;
            }
            break;
        }
      }
    },

    setHoveredOption(option) { this.hoveredOption = option; },

    unsetHoveredOption(option) {
      if (
        option !== null
        && this.hoveredOption !== null
        && this.hoveredOption[this.optionIdentity] === option[this.optionIdentity]
      ) {
        this.hoveredOption = null;
      }
    },

    onSelectOption(option, event) {
      // Single: повторный клик по уже выбранному — просто закрываем список.
      if (this.multiple === false && this.currentValue && this.isSameOption(this.currentValue, option)) {
        this.focusOut();
        return;
      }

      let value;
      if (this.multiple === true) {
        value = this.buildMultipleValue(option);
      } else {
        value = option;
      }

      // Multiple: список открыт, двигаем hover на соседа выбранной опции.
      // Single: выбор завершён, закрываем список.
      if (this.multiple === true) {
        this.moveHoverAfterSelect(option);
      } else {
        this.focusOut();
      }

      // Выбор сделан пользователем — дальше значение не пересчитываем.
      this.selectSource.markApplied(null);

      this.$emit("update:modelValue", value);
      this.$emit("input", value);
    },

    onUnselectOption(event, option) {
      if (this.multiple === false) {
        this.focusOut();
        return;
      }

      const value = this.$core.utils.isNotEmptyArray(this.currentValue)
        ? [...this.currentValue]
        : [];
      const optionIndex = value.findIndex(
        (v) => v[this.optionIdentity] === option[this.optionIdentity]
      );
      if (optionIndex > -1) {
        value.splice(optionIndex, 1);
      }

      // Действие пользователя — дальше значение не пересчитываем.
      this.selectSource.markApplied(null);

      this.$emit("update:modelValue", value);
      this.$emit("input", value);
    },

    onClear() {
      const value = this.multiple === true ? [] : null;

      // Очистка снимает метку автоподстановки: если следом значение подставят
      // заново, метку поставит сама подстановка.
      this.selectSource.markApplied(null);

      this.$emit("update:modelValue", value);
      this.$emit("input", value);
      this.$emit("blur", value);
    },

    /**
     * Подстановка значения, когда его ещё нет: единственная опция, иначе
     * дефолт источника.
     */
    autoSelectIfValueEmpty(): void {
      if (this.isValueEmpty === false) {
        return;
      }

      this.applyAutoValue();
    },

    /**
     * Единственная опция, иначе дефолт источника.
     * Заполнено значение или нет, не проверяет: решает вызывающий.
     */
    applyAutoValue(): void {
      if (this.options.length === 1) {
        this.autoSelectSingleOption();
      }
      else {
        this.autoSelectDefaultValue();
      }
    },

    /**
     * Если в списке ровно одна опция — подставляет её.
     * Только для single: выбирать нечего, ставим единственное доступное.
     * В multiple не трогаем — пустой выбор там осознанный.
     *
     * Заполнено значение или нет, не проверяет: решает вызывающий.
     */
    autoSelectSingleOption() {
      if (this.multiple === true) return;
      if (this.options.length !== 1) return;

      const single = this.options[0];
      this.selectSource.markApplied(single);
      this.$emit("update:modelValue", single);
      this.$emit("input", single);
    },

    onInput(event) {
      this.pattern = event.target.value;
    },

    /**
     * пытаемся инициализироваться дефолтным значением
     */
    autoSelectDefaultValue() {
      if (this.selectSource.hasDefault === false) { return; }

      const value = this.selectSource.defaultValue;
      // ну по идее если из defaultValue приходит null, то null нужно присваивать. Это осознанное отсутствие значения
      if (value === undefined) { return; }
      else {
        this.selectSource.markApplied(value);
        this.$emit("update:modelValue", value);
        this.$emit("input", value);
      }
    },

    /**
     * Поиск опций. На бэкенд уходит только если источник настроен на
     * loadOnInputQuery. Иначе ввод не грузит — список уже загружен.
     */
    async search(pattern) {
      if (this.selectSource.isLoadOnInputPattern === false) return;

      this.selectSource.load(pattern);
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     */
    async validate(): Promise<void> { await this.validation.validate(this.currentValue); },

    onTriangleClick(event) {
      event.stopPropagation();
      const input = this.$el.querySelector("#" + this.uid);
      if (input && typeof input.focus === "function") {
        input.focus();
      }
    },

    isValueConsistentWithOptions(): boolean {
      if (this.isValueEmpty === true) return true;

      const availableIds = new Set(this.options.map((option) => option[this.optionIdentity]));

      if (this.multiple === true) {
        return this.currentValue.every((option) => availableIds.has(option[this.optionIdentity]));
      } else {
        return availableIds.has(this.currentValue[this.optionIdentity]);
      }
    },

    // --- Утилитарные методы onSelectOption (multiple) ---

    /**
     * Собирает новое значение для multiple: копия текущего массива с toggle
     * переданной опции — если её не было, добавляет; если была, убирает.
     */
    buildMultipleValue(option) {
      const value = this.$core.utils.isNotEmptyArray(this.currentValue)
        ? [...this.currentValue]
        : [];

      const optionIndex = value.findIndex(
        (v) => v[this.optionIdentity] === option[this.optionIdentity]
      );
      if (optionIndex === -1) {
        value.push(option);
      } else {
        value.splice(optionIndex, 1);
      }

      return value;
    },

    /**
     * После выбора в multiple список остаётся открытым, а выбранная опция
     * уходит из unselectedOptions. Наводимся на соседа: если выбирали первую —
     * на следующую, иначе — на предыдущую, чтобы hover не «прыгал» вниз.
     */
    moveHoverAfterSelect(option) {
      const optionsLength = this.unselectedOptions.length;
      if (optionsLength <= 1) return;

      const optionIndex = this.unselectedOptions.findIndex(
        (item) => item[this.optionIdentity] === option[this.optionIdentity]
      );
      if (optionIndex === 0) {
        this.hoveredOption = this.unselectedOptions[1];
      } else if (optionIndex > 0 && optionIndex <= optionsLength - 1) {
        this.hoveredOption = this.unselectedOptions[optionIndex - 1];
      }
    },
  },
});
</script>
